import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import { personalizeTitle } from '../lib/formatters'
import { flattenErrorCount } from '../lib/validators'
import { apiPost } from '../lib/api'
import { dlog } from '../components/DebugOverlay'
import { isValidationDisabled } from '../lib/validationMode'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import Band from '../components/form/Band'
import StepLayout from '../components/form/StepLayout'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'

const STEP_ID = 'details'
const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml']
const ALLOWED_LABEL = 'PNG, JPG, or SVG. Max 5MB.'

// Logo is stored as { source, file?, url? }. Brandfetch/favicon URLs are
// pre-validated by the enrichment source and have no file handle to inspect,
// so only the upload branch is gated on MIME + size.
const strictSchema = z.object({
  practiceName: z
    .string()
    .trim()
    .min(2, 'Practice name must be at least 2 characters')
    .max(80, 'Practice name must be 80 characters or fewer'),
  practiceType: z.enum(['medical', 'dtc'], {
    errorMap: () => ({ message: 'Pick one' }),
  }),
  logo: z
    .any()
    .refine(
      (v) => {
        if (!v) return true
        if (v.source === 'brandfetch' || v.source === 'favicon') return true
        if (v.source === 'upload') {
          return (
            typeof File !== 'undefined' &&
            v.file instanceof File &&
            ALLOWED_TYPES.includes(v.file.type)
          )
        }
        return true
      },
      { message: 'Logo must be PNG, JPG, or SVG' },
    )
    .refine(
      (v) => {
        if (!v || v.source !== 'upload') return true
        return (
          typeof File !== 'undefined' &&
          v.file instanceof File &&
          v.file.size <= MAX_BYTES
        )
      },
      { message: 'Logo must be 5MB or smaller' },
    ),
})
const looseSchema = z.object({
  practiceName: z.any(),
  practiceType: z.any(),
  logo: z.any(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

function formatBytes(n) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

// Simple file-only dropzone used when there are no Brandfetch suggestions.
function FileDropzone({ value, onChange, error }) {
  const [dragOver, setDragOver] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!value || !(value instanceof File)) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(value)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const openPicker = () => inputRef.current?.click()
  const handleFiles = (files) => {
    const f = files?.[0]
    if (!f) return
    onChange(f)
  }
  const hasFile = value instanceof File

  return (
    <div
      className={`dropzone${dragOver ? ' dragover' : ''}${error ? ' has-error' : ''}${hasFile ? ' has-file' : ''}`}
      onClick={hasFile ? undefined : openPicker}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        handleFiles(e.dataTransfer?.files)
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!hasFile && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          openPicker()
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {hasFile ? (
        <div className="dropzone-file">
          {previewUrl && (
            <img src={previewUrl} alt="" className="dropzone-preview" />
          )}
          <div className="dropzone-meta">
            <div className="dz-name">{value.name}</div>
            <div className="dz-size">{formatBytes(value.size)}</div>
          </div>
          <button
            type="button"
            className="dropzone-remove"
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
              if (inputRef.current) inputRef.current.value = ''
            }}
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="dropzone-empty">
          <span className="dropzone-icon" aria-hidden="true">↑</span>
          <span className="dropzone-copy">
            Click to upload or drag and drop
          </span>
        </div>
      )}
    </div>
  )
}

// Brandfetch option grid + upload tile.
function LogoPicker({ value, onChange, logoOptions, error }) {
  const inputRef = useRef(null)
  const [uploadPreview, setUploadPreview] = useState(null)

  useEffect(() => {
    if (value?.source !== 'upload' || !(value.file instanceof File)) {
      setUploadPreview(null)
      return
    }
    const url = URL.createObjectURL(value.file)
    setUploadPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const pickRemote = (opt) => {
    // Preserve the source returned by the backend (brandfetch | favicon)
    // so we can treat them identically downstream.
    onChange({ source: opt?.source || 'brandfetch', url: opt.url })
  }
  const handleFiles = (files) => {
    const f = files?.[0]
    if (!f) return
    onChange({ source: 'upload', file: f })
  }
  const openPicker = () => inputRef.current?.click()

  const isRemoteSelected = (url) =>
    (value?.source === 'brandfetch' || value?.source === 'favicon') &&
    value?.url === url
  const isUploadSelected = value?.source === 'upload'

  return (
    <div className={`logo-picker${error ? ' has-error' : ''}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {logoOptions.map((opt) => (
        <button
          key={opt.url}
          type="button"
          className={`logo-picker-tile${isRemoteSelected(opt.url) ? ' is-selected' : ''}`}
          onClick={() => pickRemote(opt)}
          aria-pressed={isRemoteSelected(opt.url)}
        >
          <span className="logo-picker-thumb">
            <img src={opt.url} alt="" />
          </span>
          <span className="logo-picker-radio" aria-hidden="true" />
        </button>
      ))}
      <button
        type="button"
        className={`logo-picker-tile logo-picker-upload${isUploadSelected ? ' is-selected' : ''}`}
        onClick={openPicker}
        aria-pressed={isUploadSelected}
      >
        <span className="logo-picker-thumb">
          {uploadPreview ? (
            <img src={uploadPreview} alt="" />
          ) : (
            <span className="logo-picker-plus" aria-hidden="true">+</span>
          )}
        </span>
        <span className="logo-picker-upload-label">
          {uploadPreview ? 'Replace' : 'Upload'}
        </span>
      </button>
    </div>
  )
}

// Compact card shown once a logo has been selected (from any source).
function LogoCard({ value, onChange }) {
  const inputRef = useRef(null)
  const [uploadPreview, setUploadPreview] = useState(null)

  useEffect(() => {
    if (value?.source !== 'upload' || !(value.file instanceof File)) {
      setUploadPreview(null)
      return
    }
    const url = URL.createObjectURL(value.file)
    setUploadPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const isUpload = value?.source === 'upload'
  const imgSrc = isUpload ? uploadPreview : value?.url
  const label = isUpload
    ? value?.file?.name || 'Uploaded'
    : 'From website'

  const openPicker = () => inputRef.current?.click()
  const onFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    onChange({ source: 'upload', file: f })
  }

  return (
    <div className="logo-card">
      <div className="logo-card-thumb">
        {imgSrc && <img src={imgSrc} alt="" />}
      </div>
      <div className="logo-card-meta">
        <div className="logo-card-source">{label}</div>
      </div>
      <div className="logo-card-actions">
        <button
          type="button"
          className="logo-card-action"
          onClick={() => onChange(null)}
        >
          × Remove
        </button>
        <button
          type="button"
          className="logo-card-action"
          onClick={openPicker}
        >
          ↑ Replace
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        className="sr-only"
        onChange={onFile}
      />
    </div>
  )
}

export default function Step1Details() {
  const step = STEPS.find((s) => s.id === 'details')
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  const defaultValues = useMemo(
    () => ({
      practiceName: persisted?.practiceName ?? '',
      practiceType: liteMode ? 'dtc' : (persisted?.practiceType ?? 'medical'),
      logo: persisted?.logo ?? null,
    }),
    // We only seed on first mount; subsequent persisted updates come from this form
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  // Lite-mode progressive disclosure: hide manual fields until either the
  // user clicks "Enter manually" or runEnrich finishes. Full flow keeps
  // them visible from the start.
  const [manualVisible, setManualVisible] = useState(!liteMode)
  const practiceNameInputRef = useRef(null)


  const onValid = (data) => {
    updateFormData(STEP_ID, {
      practiceName: data.practiceName.trim(),
      practiceType: liteMode ? 'dtc' : (data.practiceType || 'medical'),
      logo: data.logo || null,
    })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  // Register submit handler with the shell nav + Enter key
  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => {
      submit()
    })
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (liteMode && !manualVisible) return
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const [enrichDomain, setEnrichDomain] = useState(suggested?.domain || '')
  const [enrichBusy, setEnrichBusy] = useState(false)
  const [failedDomain, setFailedDomain] = useState('')
  const [editingDomain, setEditingDomain] = useState(false)
  const practiceNameValue = useWatch({ control, name: 'practiceName' })

  const TOTAL_FIELDS = 5
  const fieldsFoundCount = useMemo(() => {
    if (!suggested?.domain) return 0
    let n = 0
    if (suggested.practiceName) n++
    if (
      Array.isArray(suggested.logoOptions) &&
      suggested.logoOptions.length > 0
    )
      n++
    if (suggested.phone) n++
    if (suggested.address) n++
    if (suggested.domain) n++
    return n
  }, [suggested])
  const hasSuccess = fieldsFoundCount > 0

  const viewMode = editingDomain
    ? 'editing'
    : enrichBusy
      ? 'loading'
      : failedDomain
        ? 'failure'
        : hasSuccess
          ? 'success'
          : 'idle'

  const runEnrich = async () => {
    if (enrichBusy) return
    const raw = enrichDomain.trim()
    if (!raw) return
    setEnrichBusy(true)
    setFailedDomain('')
    const res = await apiPost('/enrich-practice', { domain: raw })
    setEnrichBusy(false)
    dlog('info', '[enrich] response:', { ok: res.ok, status: res.status, body: res.data })
    if (!res.ok || !res.data?.ok) {
      setFailedDomain(raw)
      setManualVisible(true)
      return
    }
    const d = res.data.data
    dlog('info', '[enrich] data.logoOptions len:', Array.isArray(d?.logoOptions) ? d.logoOptions.length : 'not-array')
    const logoOptionsFromResp = Array.isArray(d?.logoOptions) ? d.logoOptions : []
    if (d.confidence === 'none' && logoOptionsFromResp.length === 0) {
      setFailedDomain(raw)
      setManualVisible(true)
      return
    }
    // "Edit website" is an explicit user signal to redo the prefill —
    // overwrite populated fields with whatever the new enrichment returns.
    if (d.practiceName) {
      console.log('[enrich] setting practiceName =', d.practiceName)
      setValue('practiceName', d.practiceName, {
        shouldDirty: true,
        shouldValidate: true,
      })
    } else {
      console.log('[enrich] skipping practiceName (empty)')
    }
    const normDomain = raw
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[/?#]/)[0]
    const logoOptions = Array.isArray(d.logoOptions) ? d.logoOptions : []
    dlog('info', '[enrich] writing suggested.logoOptions, len:', logoOptions.length)
    console.log('[enrich] setting suggested =', {
      domain: normDomain,
      practiceName: d.practiceName || null,
      phone: d.phone || null,
      address: d.address || null,
      logoOptionsLen: logoOptions.length,
    })
    updateFormData('suggested', {
      domain: normDomain,
      practiceName: d.practiceName || null,
      logoOptions,
      phone: d.phone || null,
      emailDomain: d.emailDomain || null,
      address: d.address || null,
    })
    if (logoOptions.length > 0) {
      const first = logoOptions[0]
      console.log('[enrich] setting logo =', first.url)
      setValue(
        'logo',
        { source: first.source || 'brandfetch', url: first.url },
        { shouldDirty: true },
      )
    } else {
      console.log('[enrich] skipping logo (no options)')
    }
    setEditingDomain(false)
    setFailedDomain('')
    setManualVisible(true)
  }

  const onEditWebsite = () => {
    setEnrichDomain(suggested?.domain || '')
    setEditingDomain(true)
  }

  const onTryDifferent = () => {
    setFailedDomain('')
    setEnrichDomain('')
    setEditingDomain(false)
  }

  const clearEnrichment = () => {
    updateFormData('suggested', {
      domain: null,
      practiceName: null,
      logoOptions: [],
      phone: null,
      emailDomain: null,
      address: null,
    })
    setValue('practiceName', '', { shouldDirty: true })
    setValue('logo', null, { shouldDirty: true })
    setEnrichDomain('')
    setFailedDomain('')
    setEditingDomain(false)
  }

  const title = personalizeTitle('Tell us about your practice')

  return (
    <form className="step-body" onSubmit={handleSubmit(onValid, onInvalid)} noValidate>
      <StepLayout question={title} helper={step.subtitle}>
      <ErrorBanner show={submitAttempted} messages={errorMessages} />
      <Band label="Find your practice" description="Enter your website to auto-fill details">
      <div
        className={`prefill-card${viewMode === 'failure' ? ' is-failure' : ''}`}
      >
        {viewMode === 'success' ? (
          <>
            <span className="prefill-card-chip">
              ✓ Filled from {suggested?.domain}
            </span>
            <p className="prefill-card-sub" style={{ marginTop: 12 }}>
              We got {fieldsFoundCount} of {TOTAL_FIELDS} fields. Review and
              finish below.
            </p>
            <div className="prefill-card-links">
              <button
                type="button"
                className="prefill-card-link"
                onClick={onEditWebsite}
              >
                Edit website
              </button>
              <button
                type="button"
                className="prefill-card-link"
                onClick={clearEnrichment}
              >
                Clear
              </button>
            </div>
          </>
        ) : viewMode === 'failure' ? (
          <>
            <h3 className="prefill-card-heading">
              Couldn't find {failedDomain}
            </h3>
            <p className="prefill-card-sub">
              No problem — fill in your details below.
            </p>
            <div className="prefill-card-links">
              <button
                type="button"
                className="prefill-card-link"
                onClick={onTryDifferent}
              >
                Try a different URL
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="prefill-card-heading">
              <span className="prefill-card-sparkle" aria-hidden="true">✨</span>
              Skip the typing
            </h3>
            <p className="prefill-card-sub">
              {liteMode
                ? "Enter your website — we'll find your practice name and logo."
                : "Enter your website — we'll prefill your practice name, logo, and contact info."}
            </p>
            <div className="prefill-card-row">
              <input
                type="url"
                inputMode="url"
                autoComplete="url"
                data-lpignore="true"
                data-form-type="other"
                className="field-input prefill-card-input"
                placeholder="yourpractice.com"
                value={enrichDomain}
                onChange={(e) => setEnrichDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    runEnrich()
                  }
                }}
                disabled={enrichBusy}
              />
              <button
                type="button"
                className="prefill-fetch-btn"
                onClick={runEnrich}
                disabled={enrichBusy || !enrichDomain.trim()}
              >
                {enrichBusy ? 'Searching…' : 'Find my practice →'}
              </button>
            </div>
          </>
        )}
      </div>

      {liteMode && !manualVisible && (
        <div className="prefill-manual-link-row">
          <button
            type="button"
            className="prefill-manual-link"
            onClick={() => {
              setManualVisible(true)
              setTimeout(() => {
                practiceNameInputRef.current?.focus()
              }, 0)
            }}
          >
            No website? Enter details manually <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
      </Band>

      {manualVisible && (
        <Band label="Practice details" description="Name and logo for your practice">
            <FieldRow cols={1}>
              <Field
                label="Practice name"
                htmlFor="practiceName"
                error={errors.practiceName}
              >
                <div className="field-input-wrapper">
                  {(() => {
                    const { ref: rhfRef, ...rhfRest } = register('practiceName')
                    return (
                      <input
                        id="practiceName"
                        type="text"
                        className={`field-input${errors.practiceName ? ' has-error' : ''}`}
                        placeholder="Practice or business name"
                        autoComplete="organization"
                        data-lpignore="true"
                        data-form-type="other"
                        {...rhfRest}
                        ref={(el) => {
                          rhfRef(el)
                          practiceNameInputRef.current = el
                        }}
                      />
                    )
                  })()}
                  {practiceNameValue && (
                    <button
                      type="button"
                      className="field-clear"
                      aria-label="Clear practice name"
                      onClick={() =>
                        setValue('practiceName', '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      ×
                    </button>
                  )}
                </div>
              </Field>
            </FieldRow>

            {/* Practice type selector removed — practiceType defaults to
                'medical' (or 'dtc' in lite mode) in the form defaults below and
                is still submitted for downstream contract logic. */}

            <FieldRow cols={1}>
              <Field
                label="Practice logo"
                error={errors.logo}
                help={`Optional. ${ALLOWED_LABEL}`}
                className="field-logo"
              >
                <Controller
                  name="logo"
                  control={control}
                  render={({ field }) => {
                    if (field.value) {
                      return (
                        <LogoCard value={field.value} onChange={field.onChange} />
                      )
                    }
                    return (
                      <FileDropzone
                        value={null}
                        onChange={(file) =>
                          field.onChange(
                            file ? { source: 'upload', file } : null,
                          )
                        }
                        error={errors.logo}
                      />
                    )
                  }}
                />
              </Field>
            </FieldRow>
        </Band>
      )}
      </StepLayout>

      {/* Hidden submit button — enables Enter-to-submit from inside the form */}
      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
